import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { HERO_IMAGE } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { t, lang } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-slate-950"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
        <img
          src={HERO_IMAGE}
          alt="ACOMEP"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/35" />
        <div className="absolute inset-0 blueprint-grid" />
      </motion.div>

      <div className="absolute top-24 right-6 lg:right-12 hidden md:flex flex-col items-end gap-1 font-mono-tech text-[10px] tracking-[0.3em] text-white/40 uppercase z-10">
        <span>BE - 50.8503° N</span>
        <span>4.3517° E</span>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-40 pb-14"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2.5 border border-white/20 bg-white/5 backdrop-blur-sm px-4 py-2 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-dot" />
          <span className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-white/80">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[1.04] text-white max-w-4xl">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-[#E31E24]" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.35 + i * 0.14,
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          key={`sub-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("#contact");
            }}
            data-testid="hero-cta-devis"
            className="group inline-flex items-center gap-3 bg-[#E31E24] hover:bg-[#C7181D] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-200"
          >
            {t.hero.cta1}
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1.5"
            />
          </a>
          <a
            href="#expertises"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("#expertises");
            }}
            data-testid="hero-cta-expertises"
            className="group inline-flex items-center gap-3 border border-white/30 hover:border-white text-white font-semibold px-7 py-4 rounded-full transition-colors duration-200"
          >
            {t.hero.cta2}
            <ArrowDownRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-white/15"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 pr-6 ${i > 0 ? "pl-6 border-l border-white/15" : ""}`}
            >
              <div className="font-display text-2xl sm:text-3xl font-bold text-white">
                {s.value}
              </div>
              <div className="mt-1 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
